import React, {useMemo, useState, useCallback, useEffect} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { datadogRum  } from '@datadog/browser-rum';

import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import ontLogo from '../ont_logo.svg';
import illuminaLogo from '../illumina_logo.svg';
import CreateIlluminaRunForm from './CreateIlluminaRunForm';
import CreateONTRunForm from './CreateONTRunForm';
import { sequencerSerialNumberMetadata } from './canonicalizeMetadata';
import { slugify } from '../utils';
import MinKNOWDirectoryUpload from './MinKNOWDirectoryUpload';
import MinKNOWUploadProgress from './MinKNOWUploadProgress';
import useMinKNOWUpload from './useMinKNOWUpload';

function getFriendlyDatasetName(userName) {
    const randomChars = Math.random().toString(36).slice(8).toUpperCase()
    const date = new Date().toISOString().split('T')[0]
    let friendlyName = `${date} Dataset ${randomChars}`

    if (userName) {
        const firstName = userName.split(' ')[0]
        friendlyName = `${firstName}'s ${friendlyName}`
    }
    
    return friendlyName
}

function validateSequencerSerialNumber(sequencer, sequencerSerialNumber) {
    // Be permissive of sequencer serial number for unknown sequencers
    if (sequencer === '' || sequencer === 'unknown' || sequencer === 'other') {
        return true
    }

    // Consider an unset serial number valid
    if (sequencerSerialNumber === '') {
        return true
    }

    return (new RegExp("_?[A-Z0-9]{5,6}(-[A-Z0-9]{1,2})?$", "i").test(sequencerSerialNumber))
}

function validateFlowCellSerialNumber(flowCell, flowCellSerialNumber) {
    // Be permissive of flow cell serial number for unknown flow cells
    if (flowCell === '' || flowCell === 'unknown' || flowCell === 'other') {
        return true
    }

    // Consider an unset serial number valid
    if (flowCellSerialNumber === '') {
        return true 
    }

    return new RegExp("^[A-Z][0-9]{5}$", "i").test(flowCellSerialNumber)
}

function AddRunDialog(props) {
    const { open, setOpen, activeProject, userData, reloadRuns, setUserErrorMessage } = props;
    const { getAccessTokenSilently } = useAuth0();

    const defaultFriendlyName = getFriendlyDatasetName();

    const [isCreating, setIsCreating] = useState(false);
    const [platform, setPlatform] = useState('ont');
    const [uploadMode, setUploadMode] = useState('minknow');
    const [friendlyName, setFriendlyName] = useState(defaultFriendlyName);
    const [name, setName] = useState(slugify(defaultFriendlyName));
    const [sequencer, setSequencer] = useState('');
    const [sequencerSerialNumber, setSequencerSerialNumber] = useState('');
    const [flowCell, setFlowCell] = useState('');
    const [flowCellSerialNumber, setFlowCellSerialNumber] = useState('');
    const [barcodingKit, setBarcodingKit] = useState('');
    const [libraryPrepKit, setLibraryPrepKit] = useState('');
    const [sequencerSerialNumberIsValid, setSequencerSerialNumberIsValid] = useState(true);
    const [flowCellSerialNumberIsValid, setFlowCellSerialNumberIsValid] = useState(true);
    const [description, setDescription] = useState('');
    const platformString = typeof window !== 'undefined' ? window.navigator?.platform || '' : '';
    const isMacPlatform = platformString.toUpperCase().includes('MAC');
    const pasteFieldInstruction = isMacPlatform
        ? 'press ⌘+Shift+G to open the directory field'
        : 'press Ctrl+L to focus the address bar';
    const pasteInstruction = isMacPlatform ? 'press ⌘+V to paste' : 'press Ctrl+V to paste';
    const pasteInstructionsText = `${pasteFieldInstruction}, then ${pasteInstruction}`;
    const handleMinKNOWSuccess = useCallback(() => {
        reloadRuns();
        setOpen(false);
    }, [reloadRuns, setOpen]);

    const handleMinKNOWError = useCallback((error) => {
        if (!error) {
            return;
        }

        let message = 'Failed to upload sequencing data. Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.';

        if (error.code === 'DIRECTORY_NOT_FOUND' || error.code === 'INVALID_PATH_STRUCTURE' || error.code === 'INVALID_FASTQ_STRUCTURE') {
            message = 'The selected Gourami directory is not valid. Select a directory with valid sequencing output.';
        } else if (error.code === 'API_ERROR'
            || error.code === 'DATASET_LOOKUP_ERROR'
            || error.code === 'EXISTING_SAMPLES_FETCH_ERROR'
            || error.code === 'SAMPLE_STATUS_FETCH_ERROR'
            || error.code === 'DATASET_UPDATE_ERROR'
            || error.code === 'SAMPLE_UPDATE_ERROR') {
            message = 'An error occurred during upload. Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.';
        } else if (error.code === 'UPLOAD_ERROR') {
            message = 'Failed to upload sequencing data. Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.';
        }

        setUploadMode('minknow');
        setUserErrorMessage?.(message);
    }, [setUserErrorMessage]);

    const {
        upload: uploadMinKNOWDirectory,
        progress: minknowProgress,
        status: minknowStatus,
        isUploading: isMinKNOWUploading,
        uploadEstimate
    } = useMinKNOWUpload({
        projectId: activeProject?.id,
        onSuccess: handleMinKNOWSuccess,
        onError: handleMinKNOWError
    });

    useEffect(() => {
        if (!isMinKNOWUploading) {
            return;
        }

        const handleBeforeUnload = (event) => {
            event.preventDefault();
            event.returnValue = 'A Gourami upload is in progress. Leaving will cancel the upload.';
            return event.returnValue;
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [isMinKNOWUploading]);

    useEffect(() => {
        const validSequencerSerialNumber = validateSequencerSerialNumber(sequencer, sequencerSerialNumber) 
        const validFlowCellSerialNumber = validateFlowCellSerialNumber(flowCell, flowCellSerialNumber)
        setSequencerSerialNumberIsValid(validSequencerSerialNumber)
        setFlowCellSerialNumberIsValid(validFlowCellSerialNumber)
    }, [flowCell, sequencer, flowCellSerialNumber, sequencerSerialNumber])

    useEffect(() => {
        setFriendlyName(getFriendlyDatasetName(userData?.data.attributes.name))
    }, [userData]);

    useEffect(() => {
        setName(slugify(friendlyName))
    }, [friendlyName]);

    useEffect(() => {
        if (!open) {
            return
        }

        const datasetName = getFriendlyDatasetName(userData?.data.attributes.name)

        setPlatform('ont');
        setUploadMode('minknow');
        setFriendlyName(datasetName)
        setName(slugify(datasetName))
        setSequencer('');
        setSequencerSerialNumber('');
        setFlowCell('');
        setFlowCellSerialNumber('');
        setBarcodingKit('');
        setLibraryPrepKit('');
        setDescription('');
    }, [open, userData])

    const handleClose = (event, reason) => {
        if (reason && reason === "backdropClick") {
            return;
        }

        if (isMinKNOWUploading) {
            return;
        }

        setOpen(false);
    };

    const handlePlatformUpdate = (event, newPlatform) => {
        if (newPlatform === null) {
            return;
        }

        setPlatform(newPlatform)
        setUploadMode(newPlatform === 'ont' ? 'minknow' : 'manual')
        setSequencer('');
        setSequencerSerialNumber('');
        setFlowCell('');
        setFlowCellSerialNumber('');
        setBarcodingKit('');
        setLibraryPrepKit('');
        setDescription('');
    };

    const createRun = useCallback(() => {
        setIsCreating(true)
        getAccessTokenSilently().then(accessToken => {
            const headers = new Headers({
              Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
            });
            const attributes = {
                name: name,
                sequencing_platform: platform,
                sequencer_name: sequencer,
            }

            if (sequencer !== '' && sequencerSerialNumber !== '') {
                attributes['minion_sn'] = `${sequencerSerialNumberMetadata[sequencer].prefix}${sequencerSerialNumber}`
            }
            
            if (flowCell !== '' && flowCell !== 'unknown' && flowCell !== 'other') {
                attributes['flowcell_name'] = flowCell
            }

            if (flowCell !== '' && flowCellSerialNumber !== '') {
                attributes['flowcell_serial_number'] = flowCellSerialNumber
            }

            if (barcodingKit !== '' && barcodingKit !== 'unknown' && barcodingKit !== 'other') {
                attributes['barcoding_kit'] = barcodingKit
            }

            if (libraryPrepKit !== '' && libraryPrepKit !== 'unknown' && libraryPrepKit !== 'other') {
                attributes['library_prep_kit'] = libraryPrepKit
            }

            if (description !== '') {
                attributes['description'] = description
            }
            return fetch(
                `${process.env.REACT_APP_KEYNOME_API_URL_BASE}/v1/projects/${activeProject.id}/sequencing_runs`,
                {
                  method: 'POST',
                  headers,
                    body: JSON.stringify(
                        {
                            data: {
                                type: "sequencing_run",
                                attributes: attributes
                            }
                        }
                    )
                },
              )
           })
           .then((res) => {
            if (!res.ok) {
                if (res.status === 422) {
                    setUserErrorMessage('Failed to create dataset record in Pathosight! Please confirm that the metadata is correct and try again. Contact pathosight-support@cepheid.com if you require assistance.');
                    return res.json();
                  }
                setUserErrorMessage('Failed to create dataset record in Pathosight! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.');
                throw new Error(`Request to ${res.url} failed with ${res.status} (${res.statusText})`)
            }
  
            return res.json()
          })
          .then(() => {
              reloadRuns()
              setIsCreating(false)
              setOpen(false)
          })
          .catch((error) => {
            setUserErrorMessage('Failed to create dataset record in Pathosight! Please wait a few minutes and try again. Contact pathosight-support@cepheid.com if you require assistance.');
            datadogRum.addError(error)
            setIsCreating(false)
        })
    }, [
        getAccessTokenSilently,
        name,
        sequencerSerialNumber,
        flowCell,
        flowCellSerialNumber,
        barcodingKit,
        libraryPrepKit,
        activeProject,
        platform,
        reloadRuns,
        sequencer,
        setOpen,
        description,
        setUserErrorMessage
    ])

    const handleMinKNOWDirectorySelected = useCallback((acceptedFiles) => {
        if (isMinKNOWUploading) return;
        uploadMinKNOWDirectory(acceptedFiles);
    }, [isMinKNOWUploading, uploadMinKNOWDirectory]);

    const isOnt = platform === 'ont';
    const showManualFields = !isOnt || uploadMode === 'manual';

    let formContent;
    if (isOnt && uploadMode === 'minknow') {
        formContent = (
            <>
                <Grid item xs={12} sx={{ padding: '15px 5px' }}>
                    <MinKNOWDirectoryUpload
                        onDirectorySelected={handleMinKNOWDirectorySelected}
                        pasteInstructions={pasteInstructionsText}
                        disabled={isMinKNOWUploading || !activeProject}
                    />
                </Grid>
                <Grid item xs={12} sx={{ padding: '0px 10px' }}>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            We will automatically extract all sequencing data from the sequencing run and upload.
                        </Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 0.5 }}>
                            To paste the directory, click the above zone, {pasteFieldInstruction}, then {pasteInstruction}.
                        </Typography>
                    </Box>
                </Grid>
            </>
        );
    } else if (isOnt && uploadMode === 'manual') {
        formContent = (
            <>
                <CreateONTRunForm
                    sequencer={sequencer}
                    setSequencer={setSequencer}
                    sequencerSerialNumber={sequencerSerialNumber}
                    setSequencerSerialNumber={setSequencerSerialNumber}
                    sequencerSerialNumberIsValid={sequencerSerialNumberIsValid}
                    flowCell={flowCell}
                    setFlowCell={setFlowCell}
                    flowCellSerialNumber={flowCellSerialNumber}
                    flowCellSerialNumberIsValid={flowCellSerialNumberIsValid}
                    setFlowCellSerialNumber={setFlowCellSerialNumber}
                    barcodingKit={barcodingKit}
                    setBarcodingKit={setBarcodingKit}
                    libraryPrepKit={libraryPrepKit}
                    setLibraryPrepKit={setLibraryPrepKit}
                    platform={platform}
                />
            </>
        );
    } else {
        formContent = (
            <CreateIlluminaRunForm 
                sequencer={sequencer}
                setSequencer={setSequencer}
                flowCell={flowCell}
                setFlowCell={setFlowCell}
                barcodingKit={barcodingKit}
                setBarcodingKit={setBarcodingKit}
                libraryPrepKit={libraryPrepKit}
                setLibraryPrepKit={setLibraryPrepKit}
                platform='illumina'
            />
        );
    }

    const formIsValid = useMemo(() => {
        return sequencerSerialNumberIsValid && flowCellSerialNumberIsValid
    }, [sequencerSerialNumberIsValid, flowCellSerialNumberIsValid]);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>Add New Sequencing Dataset</DialogTitle>
            <DialogContent>
                <Box sx={{ paddingTop: "10px" }}>
                    <Grid container>
                        <Grid item xs={12}>
                            <Box sx={{ padding: "0px 10px" }}>
                                <ToggleButtonGroup
                                    fullWidth
                                    color="primary"
                                    value={platform}
                                    exclusive
                                    onChange={handlePlatformUpdate}
                                    aria-label="Platform"
                                >
                                    <ToggleButton value="ont" data-cy="create-ont-dataset" sx={{ fontSize: "15px", fontWeight: "bold" }}>
                                        <img src={ontLogo} alt="platform-logo" height="20px" style={{ marginLeft: '4px', marginRight: '10px' }} />
                                        ONT
                                    </ToggleButton>
                                    <ToggleButton value="illumina" data-cy="create-illumina-dataset" sx={{ fontSize: "15px", fontWeight: "bold" }}>
                                        <img src={illuminaLogo} alt="platform-logo" height="20px" style={{ marginLeft: '4px', marginRight: '10px' }} />
                                        Illumina
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            </Box>
                        </Grid>
                {isOnt ? (
                    <Grid item xs={12} sx={{ padding: '0px 10px' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 1 }}>
                            {uploadMode === 'minknow' ? (
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => setUploadMode('manual')}
                                >
                                    Manually enter sequencing dataset details
                                </Link>
                            ) : (
                                <Link
                                    component="button"
                                    variant="body2"
                                    onClick={() => setUploadMode('minknow')}
                                >
                                    Copy/paste Gourami output directory
                                </Link>
                            )}
                        </Box>
                    </Grid>
                ) : null}
                        {showManualFields ? (
                            <Grid item xs={12} sx={{ padding: '15px 5px' }}>
                                <Box sx={{ padding: "5px" }}>
                                    <TextField
                                        error={name === ''}
                                        required
                                        fullWidth
                                        variant="filled"
                                        data-cy="dataset-name-input"
                                        label="Name"
                                        value={friendlyName}
                                        helperText={`Required. ${friendlyName === '' ? '' : `Identifier: ${name}`}`}
                                        onChange={(event) => {
                                            setFriendlyName(event.target.value);
                                        }}
                                    />
                                </Box>
                            </Grid>
                        ) : null}
                        {formContent}
                        {showManualFields ? (
                            <Grid item xs={12}>
                                <Box sx={{ padding: "15px 10px" }}>
                                    <TextField
                                        id="description-textarea"
                                        label="Description"
                                        placeholder="Short description of sequencing dataset"
                                        value={description}
                                        helperText="Optional"
                                        variant="standard"
                                        multiline
                                        fullWidth
                                        maxRows={2}
                                        onChange={(event) => {
                                            setDescription(event.target.value.slice(0, 100));
                                        }}
                                    />
                                </Box>
                            </Grid>
                        ) : null}
                    </Grid>
                </Box>
            </DialogContent>
            <DialogActions sx={{ marginBottom: "15px" }}>
                {showManualFields ? (
                    <Button variant="contained" color="primary" data-cy="create-dataset" sx={{ marginRight: "20px" }} onClick={createRun} disabled={(name === '' || isCreating || !formIsValid || !activeProject)}>Create</Button>
                ) : null}
                <Button color="primary" sx={{ marginRight: "20px" }} onClick={handleClose} disabled={isMinKNOWUploading}>Cancel</Button>
            </DialogActions>
            <MinKNOWUploadProgress open={isMinKNOWUploading} progress={minknowProgress} status={minknowStatus} uploadEstimate={uploadEstimate} />
        </Dialog>
    );

}


export default AddRunDialog;
